import i18nNotTypeSafe, {
  StringMap,
  TFunctionResult,
  TOptions,
  i18n
} from "i18next";
import {
  initReactI18next,
  useTranslation as useUnsafeTranslation,
  Trans as UnsafeTrans,
  TransProps,
  TFuncKey,
  Namespace,
  DefaultNamespace,
  KeyPrefix
} from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import clariti_trial_eula_en from "assets/locale/en/claritiTrialEula.json";
import locale_en from "assets/locale/en/common.json";
import validation_en from "assets/locale/en/validations.json";
import clariti_trial_eula_xx from "assets/locale/xx/claritiTrialEula.json";
import locale_xx from "assets/locale/xx/common.json";
import validation_xx from "assets/locale/xx/validations.json";
import { DefaultEnv } from "../store/env";
import * as React from "react";

const { verboseLocalLogs } = DefaultEnv;
const resources = {
  en: {
    common: locale_en,
    claritiTrialEula: clariti_trial_eula_en,
    validations: validation_en
  },
  "en-US": {
    common: locale_en,
    claritiTrialEula: clariti_trial_eula_en,
    validations: validation_en
  },
  xx: {
    common: locale_xx,
    claritiTrialEula: clariti_trial_eula_xx,
    validations: validation_xx
  }
};

export interface TypedTFunction<Keys> {
  // basic usage
  <
    TKeys extends Keys,
    // changed to `extends string` for typing
    // by default, TResult would extend TFunctionResult
    // however, this has undefined and null values
    TResult extends string, // extends Omit<TFunctionResult, "undefined" | "null"> = string,
    TInterpolationMap extends object = StringMap
  >(
    key: TKeys | TKeys[],
    options?: TOptions<TInterpolationMap> | string
  ): TResult;

  // overloaded usage
  <
    TKeys extends Keys,
    TResult extends TFunctionResult = string,
    TInterpolationMap extends object = StringMap
  >(
    key: TKeys | TKeys[],
    defaultValue?: string,
    options?: TOptions<TInterpolationMap> | string
  ): TResult;
}

type i18nTypeSafe = Omit<i18n, "t"> & { t: TypedTFunction<LocalKeys> };

i18nNotTypeSafe
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      resources,
      debug: verboseLocalLogs && process.env.NODE_ENV === "development",
      keySeparator: false,
      defaultNS: "common",
      ns: ["common", "validations", "claritiTrialEula"],
      interpolation: {
        alwaysFormat: true,
        format: function(value, format, lng) {
          if (format === "title") {
            return value;
          }
          if (!isNaN(Number(value))) {
            return Intl.NumberFormat().format(value);
          }
          return value;
        },
        escapeValue: false
      },
      fallbackLng: "en"
    },
    undefined
  );

// Allow translation key auto-completion by creating wrapper around hook with typing from en language keys
// Typing uses keys from locale_en object, as that locale is most likely have all required translation keys
export type LocalKeys =
  | keyof typeof clariti_trial_eula_en
  | keyof typeof locale_en
  | keyof typeof validation_en;

const useTranslation = () => {
  const { t: unsafeTranslate, ...rest } = useUnsafeTranslation();
  const t = (key: LocalKeys, options?: TOptions | string) =>
    unsafeTranslate(key, options);

  return { t, ...rest };
};

function Trans<
  K extends TFuncKey<N, TKPrefix> extends infer A ? A : never,
  N extends Namespace = DefaultNamespace,
  TKPrefix extends KeyPrefix<N> = undefined,
  E = React.HTMLProps<HTMLDivElement>
>(
  props: Omit<TransProps<K, N, TKPrefix, E>, "i18nKey"> & { i18nKey: LocalKeys }
): React.ReactElement {
  // admittedly hacktastic to get compliance, but works as intended and satisfies the requirement
  return UnsafeTrans(props as any);
}

/**
 * If you want to do something special if there is no translation key for the value,
 * this function allows you to translate if and only if there is an existing translation
 * entry.  If not, returns the passed in defaultValue (which defaults to undefined)
 */
function maybeTranslate<T>(
  key: LocalKeys | string,
  defaultValue?: T
): string | undefined | T {
  if (locale_en.hasOwnProperty(key)) {
    return (i18nNotTypeSafe as i18nTypeSafe).t(key as LocalKeys);
  }
  return defaultValue;
}

export { useTranslation, Trans, maybeTranslate };
export default i18nNotTypeSafe as i18nTypeSafe;
